<div class="ai-agent-keys-page p-6" data-testid="ai-agent-keys-page">
  <div class="mb-4 flex items-start justify-between gap-4">
    <div>
      <h2 class="text-2xl font-semibold text-gray-800">
        {{ 'ai_agent_keys.title' | translate }}
      </h2>
      <p class="text-sm text-gray-500 mt-1">
        {{ 'ai_agent_keys.subtitle' | translate }}
      </p>
    </div>
    @if (canCreate()) {
      <div class="flex-shrink-0 whitespace-nowrap">
        <app-button
          [label]="'ai_agent_keys.actions.create' | translate"
          type="info"
          testId="ai-agent-keys-create-btn"
          (emitOnClick)="openCreateDialog()"
        />
      </div>
    }
  </div>

  <p-table
    [value]="keys()"
    [loading]="loading()"
    dataKey="keyId"
    styleClass="p-datatable-sm"
    data-testid="ai-agent-keys-table"
  >
    <ng-template pTemplate="header">
      <tr>
        <th>{{ 'ai_agent_keys.table.channel' | translate }}</th>
        <th>{{ 'ai_agent_keys.table.key_id' | translate }}</th>
        <th>{{ 'ai_agent_keys.table.kind' | translate }}</th>
        <th>{{ 'ai_agent_keys.table.role' | translate }}</th>
        <th>{{ 'ai_agent_keys.table.status' | translate }}</th>
        <th>{{ 'ai_agent_keys.table.created_at' | translate }}</th>
        <th>{{ 'ai_agent_keys.table.created_by' | translate }}</th>
        <th>{{ 'ai_agent_keys.table.revoked_at' | translate }}</th>
        @if (canRevoke()) {
          <th class="text-center">{{ 'ai_agent_keys.table.actions' | translate }}</th>
        }
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-key let-i="rowIndex">
      <tr [attr.data-testid]="'ai-agent-keys-row-' + i">
        <td [attr.data-testid]="'ai-agent-keys-channel-' + i">
          {{ 'ai_agent_keys.channel.' + key.channel | translate }}
        </td>
        <td class="font-mono">{{ key.keyId }}</td>
        <td [attr.data-testid]="'ai-agent-keys-kind-' + i">{{ 'ai_agent_keys.kind.' + key.kind | translate }}</td>
        <td [attr.data-testid]="'ai-agent-keys-role-' + i">{{ 'ai_agent_keys.role.' + key.role | translate }}</td>
        <td>
          <p-tag
            [value]="statusLabelKey(key) | translate"
            [severity]="getStatusSeverity(key.status)"
            [attr.data-testid]="'ai-agent-keys-status-' + i"
          />
        </td>
        <td>{{ formatDateTime(key.createdAt) }}</td>
        <td>{{ key.createdBy }}</td>
        <td>{{ formatDateTime(key.revokedAt) }}</td>
        @if (canRevoke()) {
          <td class="text-center">
            @if (actionFor(key); as action) {
              <app-button
                [class]="action === 'activate' ? 'action-enable' : 'action-delete'"
                [label]="'ai_agent_keys.actions.' + action | translate"
                type="action"
                [testId]="'ai-agent-keys-' + action + '-btn-' + i"
                (emitOnClick)="confirmAction(key)"
              />
            }
          </td>
        }
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="9" class="text-center text-gray-500">
          {{ 'ai_agent_keys.table.empty' | translate }}
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>

<p-dialog
  [header]="'ai_agent_keys.dialog.create_title' | translate"
  [visible]="showCreateDialog()"
  (visibleChange)="showCreateDialog.set($event)"
  [modal]="true"
  [style]="{ width: '480px' }"
  data-testid="ai-agent-keys-create-dialog"
>
  <div class="space-y-4">
    <p class="text-sm text-gray-500">{{ 'ai_agent_keys.dialog.create_hint' | translate }}</p>
    <div>
      <label for="ai-agent-key-channel" class="block text-sm font-medium text-gray-700 mb-2">
        {{ 'ai_agent_keys.form.channel' | translate }} <span class="text-red-500">*</span>
      </label>
      <p-select
        inputId="ai-agent-key-channel"
        appendTo="body"
        class="w-full"
        [options]="channelOptions"
        optionLabel="label"
        optionValue="value"
        [placeholder]="'ai_agent_keys.form.channel_placeholder' | translate"
        [ngModel]="newChannel()"
        (ngModelChange)="onChannelChange($event)"
        [class.p-invalid]="createSubmitted() && channelMissing()"
        data-testid="ai-agent-keys-create-channel-select"
      >
        <ng-template let-option pTemplate="item">{{ option.label | translate }}</ng-template>
        <ng-template let-option pTemplate="selectedItem">{{ option.label | translate }}</ng-template>
      </p-select>
      @if (createSubmitted() && channelMissing()) {
        <p class="text-red-500 text-xs mt-1" data-testid="ai-agent-keys-create-channel-error">
          {{ 'ai_agent_keys.form.channel_required' | translate }}
        </p>
      }
    </div>
    <div>
      <label for="ai-agent-key-id" class="block text-sm font-medium text-gray-700 mb-2">
        {{ 'ai_agent_keys.form.key_id' | translate }} <span class="text-red-500">*</span>
      </label>
      <input
        pInputText
        id="ai-agent-key-id"
        class="w-full lowercase"
        [ngModel]="newKeyId()"
        (ngModelChange)="onKeyIdChange($event)"
        [class.p-invalid]="createSubmitted() && keyIdInvalid()"
        data-testid="ai-agent-keys-create-key-id-input"
      />
      <p class="text-xs text-gray-500 mt-1">{{ 'ai_agent_keys.form.key_id_hint' | translate }}</p>
      @if (createSubmitted() && keyIdInvalid()) {
        <p class="text-red-500 text-xs mt-1" data-testid="ai-agent-keys-create-key-id-error">
          {{ 'ai_agent_keys.form.key_id_invalid' | translate }}
        </p>
      }
    </div>
    @if (createError()) {
      <p-message
        severity="error"
        variant="simple"
        size="small"
        [text]="createError() || undefined"
        data-testid="ai-agent-keys-create-error"
      />
    }
  </div>
  <ng-template pTemplate="footer">
    <div class="flex justify-end gap-3">
      <app-button
        [label]="'common.actions.cancel' | translate"
        type="secondary"
        [outlined]="true"
        testId="ai-agent-keys-create-cancel-btn"
        (emitOnClick)="showCreateDialog.set(false)"
      />
      <app-button
        [label]="'ai_agent_keys.actions.create' | translate"
        type="info"
        [disabled]="creating()"
        testId="ai-agent-keys-create-submit-btn"
        (emitOnClick)="submitCreate()"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [header]="'ai_agent_keys.dialog.secret_title' | translate"
  [visible]="!!createdKey()"
  (visibleChange)="!$event && closeSecretDialog()"
  [modal]="true"
  [closable]="true"
  [closeOnEscape]="false"
  [style]="{ width: '560px' }"
  data-testid="ai-agent-keys-secret-dialog"
>
  @if (createdKey(); as created) {
    <div class="space-y-4">
      <p-message
        severity="warn"
        [text]="'ai_agent_keys.dialog.secret_warning' | translate"
        data-testid="ai-agent-keys-secret-warning"
      />
      <div>
        <div class="text-sm text-gray-600 mb-1">
          {{ 'ai_agent_keys.table.key_id' | translate }}: <span class="font-mono">{{ created.keyId }}</span>
          ({{ 'ai_agent_keys.channel.' + created.channel | translate }})
        </div>
        <code class="secret-value" data-testid="ai-agent-keys-secret-value">{{ created.secret }}</code>
      </div>
    </div>
  }
  <ng-template pTemplate="footer">
    <div class="flex justify-end gap-3">
      <app-button
        [label]="'ai_agent_keys.actions.copy' | translate"
        type="info"
        [outlined]="true"
        icon="content_copy"
        testId="ai-agent-keys-secret-copy-btn"
        (emitOnClick)="copySecret()"
      />
      <app-button
        [label]="'ai_agent_keys.actions.done' | translate"
        type="info"
        testId="ai-agent-keys-secret-done-btn"
        (emitOnClick)="closeSecretDialog()"
      />
    </div>
  </ng-template>
</p-dialog>

<p-toast />

<app-confirmation-dialog
  [visible]="showConfirmationDialog()"
  (visibleChange)="showConfirmationDialog.set($event)"
  [title]="confirmationTitle()"
  [message]="confirmationMessage()"
  [buttons]="confirmationButtons()"
  (emitOnClick)="onConfirmationDialogAction($event)"
/>
